import { Divider, Skeleton } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useGuard } from '@affine/core/components/guard';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { getOfflineStatusCopy } from '@affine/core/components/offline-status';
import { ServerService } from '@affine/core/modules/cloud';
import { DocService } from '@affine/core/modules/doc';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { onBackendAvailable } from '../../../../utils/backend-availability';
import { CloudSvg } from '../cloud-svg';
import { CopyLinkButton } from './copy-link-button';
import { MembersPermission, PublicDoc } from './general-access';
import * as styles from './index.css';
import { InviteInput } from './invite-member-editor';
import { MembersRow } from './member-management';
import type { ShareMenuProps } from './share-menu';
import { useDocShareRestrictions } from './use-doc-share-restrictions';

export const LocalSharePage = (props: ShareMenuProps) => {
  const t = useI18n();
  const {
    workspaceMetadata: { id: workspaceId },
  } = props;
  return (
    <>
      <div className={styles.localSharePage}>
        <div className={styles.columnContainerStyle} style={{ gap: '12px' }}>
          <div
            className={styles.descriptionStyle}
            style={{ maxWidth: '230px' }}
          >
            {t['com.streamlain.share-menu.EnableCloudDescription']()}
          </div>
          <div>
            <Button
              onClick={props.onEnableAffineCloud}
              variant="primary"
              data-testid="share-menu-enable-affine-cloud-button"
            >
              {t['Enable AFFiNE Cloud']()}
            </Button>
          </div>
        </div>
        <div className={styles.cloudSvgContainer}>
          <CloudSvg />
        </div>
      </div>
      <CopyLinkButton workspaceId={workspaceId} secondary />
    </>
  );
};

export const AFFiNESharePage = (
  props: ShareMenuProps & {
    hideMemberControls?: boolean;
    onClickInvite?: () => void;
    onClickMembers?: () => void;
  }
) => {
  const t = useI18n();
  const {
    workspaceMetadata: { id: workspaceId },
  } = props;
  const shareInfoService = useService(ShareInfoService);
  const serverService = useService(ServerService);
  const docService = useService(DocService);
  const connectivityStatus = useConnectivityStatus();
  const isOffline = useUiOfflineSignal(connectivityStatus.offline);
  const { isReady: isFolderCacheReady, privateFolderDocShareBlocked } =
    useDocShareRestrictions();

  const canManageUsers = useGuard('Doc_Users_Manage', docService.doc.id);

  const canPublish = useGuard('Doc_Publish', docService.doc.id);

  useEffect(() => {
    shareInfoService.shareInfo.revalidate();
    return onBackendAvailable(() => {
      shareInfoService.shareInfo.revalidate();
    });
  }, [shareInfoService]);

  const isSharedPage = useLiveData(shareInfoService.shareInfo.isShared$);
  const sharedMode = useLiveData(shareInfoService.shareInfo.sharedMode$);
  const baseUrl = serverService.server.baseUrl;
  const isLoading =
    isSharedPage === null ||
    sharedMode === null ||
    baseUrl === null ||
    !isFolderCacheReady;

  if (isOffline && isLoading) {
    return (
      <div className={styles.content}>
        <div className={styles.privateFolderWarning}>
          {getOfflineStatusCopy(t, 'share-load-online-only')}
        </div>
      </div>
    );
  }

  if (isLoading) {
    // TODO(@eyhn): loading and error UI
    return (
      <>
        <Skeleton height={100} />
        <Skeleton height={40} />
      </>
    );
  }

  return (
    <div className={styles.content}>
      <div className={styles.columnContainerStyle}>
        <div className={styles.memberRowsStyle}>
          {privateFolderDocShareBlocked && (
            <div className={styles.privateFolderWarning}>
              {t[
                'com.streamlain.share-menu.invite-editor.private-folder-doc-disabled'
              ]()}
            </div>
          )}
          {isOffline && (
            <div className={styles.privateFolderWarning}>
              {getOfflineStatusCopy(t, 'share-load-online-only')}
            </div>
          )}
          {!props.hideMemberControls &&
            canManageUsers &&
            props.onClickInvite && (
              <InviteInput
                onFocus={props.onClickInvite}
                disabled={isOffline || privateFolderDocShareBlocked}
              />
            )}
          {!props.hideMemberControls && props.onClickMembers ? (
            <MembersRow onClick={props.onClickMembers} />
          ) : null}
        </div>

        <div className={styles.generalAccessStyle}>
          {t['com.streamlain.share-menu.generalAccess']()}
        </div>
        <MembersPermission
          disabled={
            isOffline || !canManageUsers || privateFolderDocShareBlocked
          }
        />
        <PublicDoc
          disabled={isOffline || !canPublish || privateFolderDocShareBlocked}
        />
      </div>
      <Divider className={styles.divider} />
      <CopyLinkButton workspaceId={workspaceId} />
    </div>
  );
};

export const SharePage = (
  props: ShareMenuProps & {
    hideMemberControls?: boolean;
    onClickInvite?: () => void;
    onClickMembers?: () => void;
  }
) => {
  if (props.workspaceMetadata.flavour === 'local') {
    return <LocalSharePage {...props} />;
  } else {
    return (
      // TODO(@eyhn): refactor this part
      <ErrorBoundary
        fallback={null}
        onError={error => {
          console.error('[ShareMenu] Share page render failed:', error);
        }}
      >
        <Suspense>
          <AFFiNESharePage {...props} />
        </Suspense>
      </ErrorBoundary>
    );
  }
};
